import React, { useCallback, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';

// Styles & Assets
import styles from './PatientPollsPage.module.css';
import { ReactComponent as UpdateSvg } from '../../assets/icons/update.svg';

// Services
import { sortByAttribute } from '../../services/sort';
import { getEnvironmentApiUrl } from '../../services/General';
import { getProfessionalInquiryType, InquiryTypes } from '../../services/Poll';
import { canEditCaseView } from '../../services/user';
import { getFullName } from '../../services/getPatientAtribute';

// Hooks
import { useState } from 'react';

// Components
import ErrorSpinnerWrapper from '../../components/UI/ErrorSpinnerWrapper.tsx/ErrorSpinnerWrapper';
import ButtonsPolls from '../../components/Patient/ButtonsPolls/ButtonsPolls';
import FilterButtonPolls from '../../components/PatientPollsPage/FilterButtonPolls/FilterButtonPolls';
import ButtonDialog from '../../components/Helpers/ButtonDialog/ButtonDialog';
import DialogTestPoll from '../../components/PatientPollsPage/DialogTestPoll/DialogTestPoll';
import useHttp from '../../hooks/useHttp';
import InquirySlot from '../../components/PatientPollsPage/InquirySlot/InquirySlot';
import AnswerAssignedInquiry from '../../components/PatientPollsPage/AnswerAssignedInquiry/AnswerAssignedInquiry';
import AnswerOnDemandInquiry from '../../components/PatientPollsPage/AnswerOnDemandInquiry/AnswerOnDemandInquiry';
import ResultsPollPatientDialog from '../../components/PatientPollsPage/ResultsPollPatientDialog/ResultsPollPatientDialog';
import Modal from '../../components/UI/Modal/Modal';

// Interfaces
import { AssignedInquiry, LinkedInquiry } from '../../interfaces/Poll';
import { PatientOutletContext } from '../../interfaces/Patient';

// Other
import { useOutletContext } from 'react-router-dom';
import moment from 'moment';

const TIMER = 5000; //getDefaultTimerMiliSeconds();

const PatientPollsPage: React.FC = () => {
    const { caso, patient }: PatientOutletContext = useOutletContext();

    const [isFirstRender, setIsFirstRender] = useState(true);

    const [typePoll, setTypePoll] = useState<InquiryTypes[]>([]);
    const [startDate, setStartDate] = useState<moment.Moment | null>(null);
    const [endDate, setEndDate] = useState<moment.Moment | null>(null);

    const [inquiryShow, setInquiryShow] = useState<AssignedInquiry | null>(null);
    const [patientInquiryShow, setPatientInquiryShow] = useState<LinkedInquiry | null>(null);

    const [seeOnlyAnswered, setSeeOnlyAnswered] = useState(false);

    const [showModalAnswers, setShowModalAnswers] = useState(false);
    const [patientInquirys, setPatientInquirys] = useState<LinkedInquiry[]>([]);
    const [inquiries, setInquiries] = useState<AssignedInquiry[]>([]);
    const [showDialogResults, setShowModalResults] = useState<boolean>(false);

    const { isLoading, error, sendRequest } = useHttp<AssignedInquiry[]>();
    const {
        isLoading: loadingPatientInquiry,
        error: errorPatientInquiry,
        sendRequest: getPatientInquiry
    } = useHttp<LinkedInquiry[]>();

    const caseId = caso?.id;

    const getInquiries = useCallback(() => {
        if (!caseId) return;

        getPatientInquiry({
            url: getEnvironmentApiUrl() + 'patient_inquiry/all',
            method: 'POST',
            body: {
                filter: { case_id: caseId, end_date: null, frecuency: -1 },
                params: {},
            },
        }, (data) => setPatientInquirys(data));

        sendRequest({
            url: getEnvironmentApiUrl() + 'patient_inquiries/all',
            method: 'POST',
            body: {
                filter: { case_id: caseId },
                params: {},
            },
        }, (data) => setInquiries(sortByAttribute(data, 'assigned_date', false)));
    }, [caseId, getPatientInquiry, sendRequest]);

    useEffect(() => {
        if (!caseId) return;

        getInquiries();

        const timer = setInterval(() => {
            setIsFirstRender(false);
            getInquiries();
        }, TIMER);

        return () => clearInterval(timer);
    }, [caseId, getInquiries]);

    const clearInquiryRefs = () => {
        setInquiryShow(null);
        setPatientInquiryShow(null);
    };

    const handleShowDialog = (inquiry: AssignedInquiry) => {
        clearInquiryRefs();
        setInquiryShow(inquiry);
        if (inquiry.filled_date) setShowModalResults(true);
        else setShowModalAnswers(true);
    };

    const handleShowDemandModal = (patientInquiry: LinkedInquiry) => {
        clearInquiryRefs();
        setPatientInquiryShow(patientInquiry);
        setShowModalAnswers(true);
    };

    const setFilledDate = (pollID: number, date: string) => {
        setInquiries(oldInquiries =>
            oldInquiries.map(oldInquiry =>
                oldInquiry.id === pollID
                    ? { ...oldInquiry, filled_date: date }
                    : oldInquiry
            )
        );
    };

    const addPatientInquiry = (inquiry: AssignedInquiry) => {
        setInquiries(oldInquiries => [inquiry, ...oldInquiries]);
    };

    const onConfirm = (type: InquiryTypes[], seeOnlyAnswered: boolean, fromDate: moment.Moment | null, toDate: moment.Moment | null) => {
        setTypePoll(type);
        setSeeOnlyAnswered(seeOnlyAnswered);
        setStartDate(fromDate);
        setEndDate(toDate);
    };

    const filterFunc = useCallback((i: AssignedInquiry) => {
        if (seeOnlyAnswered && i.filled_date === null) return false;
        if (typePoll.length !== 0 && i.inquiry && !typePoll.includes(getProfessionalInquiryType(i.inquiry))) return false;
        const iDate = moment(i.assigned_date).second(0).millisecond(0);
        if (startDate !== null && moment(iDate).isBefore(startDate)) return false;
        if (endDate !== null && moment(iDate).isAfter(endDate)) return false;
        return true;
    }, [seeOnlyAnswered, typePoll, startDate, endDate]);

    const filteredInquiries = useMemo(() => {
        return inquiries.filter(i => filterFunc(i));
    }, [inquiries, filterFunc]);

    const searchbarElement = document.getElementById('patient-buttons-right') as HTMLElement;

    if (!caso || !patient) return null;

    const canEdit = canEditCaseView(caso);
    const answerOnDemandInquiry = (assignedInquiry: AssignedInquiry) => {
        setInquiries(oldInquiries => {
            let foundInquiry = false;
            const newInquiries = [...oldInquiries].map(oldInquiry => {
                if (oldInquiry.id !== assignedInquiry.id) return oldInquiry;
                foundInquiry = true;
                return assignedInquiry;
            });
            if (foundInquiry) return newInquiries;
            else return [assignedInquiry, ...newInquiries]
        });
    };

    return (
        <div className='fitContainer'>
            <ErrorSpinnerWrapper
                error={error || errorPatientInquiry}
                loading={isFirstRender && (isLoading || loadingPatientInquiry)}
            >
                {searchbarElement && ReactDOM.createPortal(
                    <div className={styles.containerButtons}>
                        <ButtonsPolls patientID={patient.id} caseID={caso.id} readOnly={!canEdit} />
                        <FilterButtonPolls
                            defaultTypePoll={typePoll}
                            defaultSeeAnswered={seeOnlyAnswered}
                            defaultFromDate={startDate}
                            defaultToDate={endDate}
                            onConfirm={onConfirm}
                        />
                        {window.config.DIFFERENCE_TESTPROM_POLLS &&
                            <ButtonDialog
                                readOnly={!canEdit}
                                buttonComponent={
                                    <div className={styles.testButton}>
                                        {InquiryTypes.TEST}
                                    </div>
                                }
                                renderDialog={({ setShowDialog }) =>
                                    <DialogTestPoll
                                        patientID={patient.id}
                                        caseID={caso.id}
                                        closeDialog={() => setShowDialog(false)}
                                        addPatientInquiry={addPatientInquiry}
                                    />
                                }
                            />
                        }
                    </div>,
                    searchbarElement,
                )}
                <div className={styles.inquiriesList}>
                    {!seeOnlyAnswered && patientInquirys.map(patientInquiry =>
                        <InquirySlot
                            key={patientInquiry.id}
                            label={<div className={styles.onDemandContainer}>
                                {patientInquiry.inquiry?.name ?? ''}
                                <UpdateSvg className={styles.updateIcon} />
                            </div>}
                            date={patientInquiry.start_date}
                            isAnswered={false}
                            disabled={!canEdit}
                            onClick={() => handleShowDemandModal(patientInquiry)}
                        />
                    )}
                    {filteredInquiries.map(inquiry =>
                        <InquirySlot
                            key={inquiry.id}
                            label={<InquiryLabel inquiry={inquiry} />}
                            date={inquiry.assigned_date}
                            isAnswered={inquiry.filled_date !== null}
                            disabled={!canEdit && inquiry.filled_date === null}
                            onClick={() => handleShowDialog(inquiry)}
                        />
                    )}
                </div>
            </ErrorSpinnerWrapper>

            {(inquiryShow || patientInquiryShow) && (
                <>
                    <Modal show={showModalAnswers} setShowModal={setShowModalAnswers}>
                        {inquiryShow ? (
                            <AnswerAssignedInquiry
                                patientName={getFullName(patient)}
                                patient_id={patient.id}
                                assignedInquiry={inquiryShow}
                                //setShowDialog={setShowModalAnswers}
                                setFilledDate={setFilledDate}
                                closeModal={() => setShowModalAnswers(false)}
                            />
                        ) : patientInquiryShow ? (
                            <AnswerOnDemandInquiry
                                caseID={caso.id}
                                patient={patient}
                                patientInquiry={patientInquiryShow}
                                setShowModal={setShowModalAnswers}
                                answerOnDemandInquiry={answerOnDemandInquiry}
                            />
                        ) : null}
                    </Modal>

                    <Modal show={showDialogResults} setShowModal={setShowModalResults}>
                        {inquiryShow && (
                            <ResultsPollPatientDialog
                                patient_id={patient.id}
                                patientInquiriesID={inquiryShow.id}
                                setShowDialog={setShowModalResults}
                            />
                        )}
                    </Modal>
                </>
            )}
        </div>
    );
};

export default PatientPollsPage;

const InquiryLabel = ({ inquiry }: { inquiry: AssignedInquiry }) => {
    return (
        <>
            <strong>
                {window.config.DIFFERENCE_TESTPROM_POLLS && inquiry.inquiry &&
                    <>
                        <span className={styles.inquiryType}>
                            {getProfessionalInquiryType(inquiry.inquiry)}
                        </span>{' '}
                    </>
                }
            </strong>
            {inquiry.inquiry?.name ?? ''}
        </>
    );
};